import { css } from '@emotion/react'
import { type ReactElement, useCallback, useEffect, useState } from 'react'
import { SpacingToken } from '@livechat/design-system-react-components'
import { useSnapCarousel } from 'react-snap-carousel'

import { CAROUSEL_TYPES } from '@/constants'
import { usePreventScroll } from '@/utils/browser'
import type { CarouselItemProps, MediaSlide, Slide } from './interfaces'
import { MediaPreview } from './MediaPreview'
import { ChevronPrev } from './ChevronPrev'
import { ChevronNext } from './ChevronNext'

const wrapperCss = css`
  position: relative;
`

const scrollContainerCss = css`
  display: flex;
  align-items: center;
  overflow: auto;
  position: relative;
  list-style: none;
  column-gap: var(${SpacingToken.Spacing6});
  padding: 0;
  margin: 0;

  scrollbar-width: none;
  overscroll-behavior: contain auto;
  scroll-snap-type: x mandatory;

  /* Hide scrollbar for Safari */
  &::-webkit-scrollbar {
    display: none;
  }
`

interface CarouselRenderItemProps<T> {
  readonly item: T
  readonly isSnapPoint: boolean
  readonly preview?: boolean
  readonly handleClick?: () => void
}

interface Props<T extends Slide> {
  readonly items: T[]
  readonly renderItem: (
    props: CarouselRenderItemProps<T>
  ) => ReactElement<CarouselItemProps>
  readonly preview?: boolean
  readonly className?: string
}

const areMediaSlides = (slides: Slide[]): slides is MediaSlide[] =>
  slides.every((slide) => {
    return (
      slide.type === CAROUSEL_TYPES.VIDEO || slide.type === CAROUSEL_TYPES.IMAGE
    )
  })

export const Carousel = <T extends Slide>({
  items,
  renderItem,
  preview,
  className,
}: Props<T>) => {
  const {
    scrollRef,
    hasPrevPage,
    hasNextPage,
    prev,
    next,
    snapPointIndexes,
    pages,
    activePageIndex,
    goTo,
  } = useSnapCarousel()
  const [selectedSlide, setSelectedSlide] = useState<number | null>(null)
  const handlePrevious = useCallback(() => prev(), [prev])
  const handleNext = useCallback(() => next(), [next])

  const isMediaPreviewOpen = selectedSlide !== null

  usePreventScroll(isMediaPreviewOpen)

  // Handle Media Preview Carousel page change
  const handlePreviousCarouselPage = useCallback(
    (prevItemIndex: number) => {
      if (pages[activePageIndex].includes(prevItemIndex)) {
        return
      }

      const prevPageIndex =
        activePageIndex === 0 ? pages.length - 1 : activePageIndex - 1
      goTo(prevPageIndex)
    },
    [activePageIndex, goTo, pages]
  )
  const handleNextCarouselPage = useCallback(
    (nextItemIndex: number) => {
      if (pages[activePageIndex].includes(nextItemIndex)) {
        return
      }

      const nextPageIndex =
        activePageIndex === pages.length - 1 ? 0 : activePageIndex + 1
      goTo(nextPageIndex)
    },
    [activePageIndex, goTo, pages]
  )

  // Handle Media Preview slide change
  const handlePreviousPreviewSlide = useCallback(() => {
    setSelectedSlide((slide) => {
      const prevIndex = slide ? slide - 1 : items.length - 1
      handlePreviousCarouselPage(prevIndex)

      return prevIndex
    })
  }, [handlePreviousCarouselPage, items.length])
  const handleNextPreviewSlide = useCallback(() => {
    setSelectedSlide((slide) => {
      if (slide === null) {
        return null
      }

      const nextIndex = slide === items.length - 1 ? 0 : slide + 1
      handleNextCarouselPage(nextIndex)

      return nextIndex
    })
  }, [handleNextCarouselPage, items.length])

  // Media Preview interaction
  const handleCloseMediaPreview = useCallback(() => setSelectedSlide(null), [])
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (selectedSlide !== null) {
        switch (e.key) {
          case 'ArrowLeft':
            handlePreviousPreviewSlide()
            break

          case 'ArrowRight':
            handleNextPreviewSlide()
            break

          default:
        }
      }
    },
    [handleNextPreviewSlide, handlePreviousPreviewSlide, selectedSlide]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  return (
    <div css={wrapperCss} className={className}>
      {hasPrevPage && <ChevronPrev handlePrev={handlePrevious} />}
      {hasNextPage && <ChevronNext handleNext={handleNext} />}

      <ul css={scrollContainerCss} ref={scrollRef}>
        {items.map((item, index) => {
          const handleRenderItemClick = (index: number) => {
            if (!preview) {
              return
            }

            setSelectedSlide(index)
          }

          return renderItem({
            item,
            isSnapPoint: snapPointIndexes.has(index),
            preview,
            handleClick: () => handleRenderItemClick(index),
          })
        })}
      </ul>

      {isMediaPreviewOpen && areMediaSlides(items) && (
        <MediaPreview
          handleClose={handleCloseMediaPreview}
          mediaItem={items[selectedSlide]}
          mediaList={items}
          prevImg={handlePreviousPreviewSlide}
          nextImg={handleNextPreviewSlide}
        />
      )}
    </div>
  )
}
